import { useCallback, useEffect, useRef, useState } from "preact/hooks"

export type RecorderKind = "audio" | "video"

export interface UseMediaRecorder {
	recording: boolean
	elapsedSeconds: number
	error: string
	/** Live camera/mic stream while recording — bind to a <video>'s
	 *  srcObject for a video preview. */
	stream: MediaStream | null
	blob: Blob | null
	mimeType: string
	start: (kind: RecorderKind) => Promise<void>
	stop: () => void
	reset: () => void
}

/** Prefers mp4 (Safari-friendly) and falls back to webm, per specs/plan.md. */
function pickMimeType(kind: RecorderKind): string {
	const candidates =
		kind === "video" ? ["video/mp4", "video/webm"] : ["audio/mp4", "audio/webm"]
	for (const candidate of candidates) {
		if (
			typeof MediaRecorder !== "undefined" &&
			MediaRecorder.isTypeSupported?.(candidate)
		) {
			return candidate
		}
	}
	return candidates[candidates.length - 1]
}

export function useMediaRecorder(): UseMediaRecorder {
	const [recording, setRecording] = useState(false)
	const [elapsedSeconds, setElapsedSeconds] = useState(0)
	const [error, setError] = useState("")
	const [stream, setStream] = useState<MediaStream | null>(null)
	const [blob, setBlob] = useState<Blob | null>(null)
	const [mimeType, setMimeType] = useState("")

	const recorderRef = useRef<MediaRecorder | null>(null)
	const chunksRef = useRef<Blob[]>([])
	const timerRef = useRef<number | null>(null)
	const streamRef = useRef<MediaStream | null>(null)

	const stopTracks = useCallback(() => {
		streamRef.current?.getTracks().forEach((track) => track.stop())
		streamRef.current = null
	}, [])

	const clearTimer = useCallback(() => {
		if (timerRef.current !== null) {
			window.clearInterval(timerRef.current)
			timerRef.current = null
		}
	}, [])

	// Stop all tracks and timers on unmount so the camera/mic indicator
	// doesn't stay lit if the user navigates away mid-recording.
	useEffect(() => {
		return () => {
			clearTimer()
			stopTracks()
			if (recorderRef.current && recorderRef.current.state !== "inactive") {
				recorderRef.current.stop()
			}
		}
	}, [clearTimer, stopTracks])

	const start = useCallback(
		async (kind: RecorderKind) => {
			setError("")
			setBlob(null)
			chunksRef.current = []
			try {
				// Capture at 720p: that is what gets stored and played back, so
				// recording at the camera's full resolution would only mean a
				// slower upload on a phone connection and a bigger transcode.
				const constraints: MediaStreamConstraints =
					kind === "video"
						? {
								audio: true,
								video: {
									facingMode: "user",
									width: { ideal: 1280 },
									height: { ideal: 720 },
								},
							}
						: { audio: true, video: false }
				const media = await navigator.mediaDevices.getUserMedia(constraints)
				streamRef.current = media
				setStream(media)

				const picked = pickMimeType(kind)
				setMimeType(picked)

				const recorder = new MediaRecorder(media, { mimeType: picked })
				recorder.ondataavailable = (event) => {
					if (event.data.size > 0) chunksRef.current.push(event.data)
				}
				recorder.onstop = () => {
					setBlob(new Blob(chunksRef.current, { type: picked }))
					stopTracks()
					setStream(null)
				}
				recorderRef.current = recorder
				recorder.start()
				setRecording(true)
				setElapsedSeconds(0)
				clearTimer()
				timerRef.current = window.setInterval(() => {
					setElapsedSeconds((s) => s + 1)
				}, 1000)
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: "Couldn't access the camera/microphone",
				)
			}
		},
		[clearTimer, stopTracks],
	)

	const stop = useCallback(() => {
		clearTimer()
		setRecording(false)
		if (recorderRef.current && recorderRef.current.state !== "inactive") {
			recorderRef.current.stop()
		}
	}, [clearTimer])

	const reset = useCallback(() => {
		clearTimer()
		stopTracks()
		recorderRef.current = null
		chunksRef.current = []
		setStream(null)
		setBlob(null)
		setRecording(false)
		setElapsedSeconds(0)
		setError("")
	}, [clearTimer, stopTracks])

	return {
		recording,
		elapsedSeconds,
		error,
		stream,
		blob,
		mimeType,
		start,
		stop,
		reset,
	}
}
